import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react"
import {
  ESTIMATE_DRAFT_KEY,
  type QEDraft,
  type QEPhoto,
  type VinDecodedVehicle,
} from "./types"
import {
  newTagFromPanel,
  type DamageTag,
  type PanelSpec,
} from "./damage-tag/panels"

// Quick Estimate 3 — state hook.
//
// Persists to *sessionStorage* (not localStorage) so an in-tab
// refresh restores the flow but closing the tab kills it. Matches
// the service-line model: one estimate, one sitting, no banners.
//
// File references for in-flight or failed uploads are NOT persisted —
// File objects can't survive a page reload. Photos restored from
// sessionStorage with empty uploadedUrl render as "Couldn't tag,
// remove and retake" with no Retry option (the file's gone). The
// step-photos component owns the live File map.

export interface QuickEstimateCustomer {
  name: string
  phone: string
}

// 0 = Vehicle, 1 = Photos (quality only), 2 = Tags (AI side review),
// 3 = Send. Splitting photos and tags lets the photo step focus on
// "is this shot usable?" while tags is purely "did the AI label it
// right?" — two questions, two surfaces.
export type QuickEstimateStepIndex = 0 | 1 | 2 | 3

export interface QuickEstimateState {
  vin: string
  vehicle: VinDecodedVehicle | null
  photos: QEPhoto[]
  /** Pre-photo damage tags from the 3D tagger. Drives the structured
   *  context sent to the LLM (panel + side + severity + type). */
  damageTags: DamageTag[]
  notes: string
  customer: QuickEstimateCustomer
  /** Last step the wizard rendered. Hydrated from sessionStorage so
   *  an in-tab refresh lands on the same step the advisor was on. */
  step: QuickEstimateStepIndex
}

type Action =
  | { type: "set-vin"; vin: string }
  | { type: "set-vehicle"; vehicle: VinDecodedVehicle | null }
  | { type: "add-photo"; photo: QEPhoto }
  | { type: "update-photo"; id: string; patch: Partial<QEPhoto> }
  | { type: "remove-photo"; id: string }
  | { type: "toggle-damage-tag"; panel: PanelSpec }
  | { type: "remove-damage-tag"; panelId: string }
  | { type: "update-damage-tag"; panelId: string; patch: Partial<DamageTag> }
  | { type: "set-notes"; notes: string }
  | { type: "set-customer"; customer: Partial<QuickEstimateCustomer> }
  | { type: "set-step"; step: QuickEstimateStepIndex }
  | { type: "hydrate"; state: QuickEstimateState }
  | { type: "reset" }

const INITIAL: QuickEstimateState = {
  vin: "",
  vehicle: null,
  photos: [],
  damageTags: [],
  notes: "",
  customer: { name: "", phone: "" },
  step: 0,
}

function reducer(state: QuickEstimateState, action: Action): QuickEstimateState {
  switch (action.type) {
    case "set-vin":
      return { ...state, vin: action.vin }
    case "set-vehicle":
      return { ...state, vehicle: action.vehicle }
    case "add-photo":
      return { ...state, photos: [...state.photos, action.photo] }
    case "update-photo":
      return {
        ...state,
        photos: state.photos.map((p) =>
          p.id === action.id ? { ...p, ...action.patch } : p,
        ),
      }
    case "remove-photo":
      return {
        ...state,
        photos: state.photos.filter((p) => p.id !== action.id),
      }
    case "toggle-damage-tag": {
      const exists = state.damageTags.some((t) => t.panelId === action.panel.id)
      return {
        ...state,
        damageTags: exists
          ? state.damageTags.filter((t) => t.panelId !== action.panel.id)
          : [...state.damageTags, newTagFromPanel(action.panel)],
      }
    }
    case "remove-damage-tag":
      return {
        ...state,
        damageTags: state.damageTags.filter((t) => t.panelId !== action.panelId),
      }
    case "update-damage-tag":
      return {
        ...state,
        damageTags: state.damageTags.map((t) =>
          t.panelId === action.panelId ? { ...t, ...action.patch } : t,
        ),
      }
    case "set-notes":
      return { ...state, notes: action.notes }
    case "set-customer":
      return {
        ...state,
        customer: { ...state.customer, ...action.customer },
      }
    case "set-step":
      return { ...state, step: action.step }
    case "hydrate":
      return action.state
    case "reset":
      return INITIAL
    default:
      return state
  }
}

export function useQuickEstimateState() {
  const storageKey = ESTIMATE_DRAFT_KEY
  const [state, dispatch] = useReducer(reducer, INITIAL)
  // Whether sessionStorage hydration has already run. The wizard
  // looks at this to avoid blinking through step 0 before the
  // restore kicks in.
  const [hydrated, setHydrated] = useState(false)

  // Hydrate once on mount.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(storageKey)
      if (!raw) {
        setHydrated(true)
        return
      }
      const parsed = JSON.parse(raw) as QEDraft
      if (parsed.v !== 2) {
        sessionStorage.removeItem(storageKey)
        setHydrated(true)
        return
      }
      // 1-hour ceiling on a session draft. sessionStorage already
      // dies on tab close so this is a defense in depth, not a
      // primary TTL.
      if (Date.now() - parsed.updatedAt > 60 * 60 * 1000) {
        sessionStorage.removeItem(storageKey)
        setHydrated(true)
        return
      }
      dispatch({
        type: "hydrate",
        state: {
          vin: parsed.vin,
          vehicle: parsed.vehicle,
          photos: parsed.photos.map((p) => ({
            ...p,
            // Local objectURLs are dead after refresh — re-seed from
            // the CDN URL when we have one, otherwise the photo will
            // render in its empty/broken state and the component
            // copy will surface that.
            previewUrl: p.uploadedUrl || p.previewUrl,
          })),
          damageTags: parsed.damageTags ?? [],
          notes: parsed.notes,
          customer: parsed.customer,
          step: parsed.step,
        },
      })
    } catch {
      // Corrupted JSON — silent reset.
      try {
        sessionStorage.removeItem(storageKey)
      } catch {
        // ignore
      }
    } finally {
      setHydrated(true)
    }
    // Run-once on mount; deps intentionally empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Persist on every change once hydration has run.
  // ─── DISABLED ─────────────────────────────────────────────────────
  // sessionStorage save is commented out so drafts no longer survive
  // an in-tab refresh. The hydrate path above and the auto-clean / TTL
  // logic stay in place, so re-enabling this block is the only change
  // needed to bring back draft caching.
  const isFirstRender = useRef(true)
  useEffect(() => {
    if (!hydrated) return
    if (isFirstRender.current && state === INITIAL) {
      isFirstRender.current = false
      return
    }
    isFirstRender.current = false
    // try {
    //   const draft: QEDraft = {
    //     v: 2,
    //     vin: state.vin,
    //     vehicle: state.vehicle,
    //     photos: state.photos.map((p) => ({
    //       ...p,
    //       previewUrl: p.uploadedUrl || p.previewUrl,
    //     })),
    //     notes: state.notes,
    //     customer: state.customer,
    //     step: state.step,
    //     updatedAt: Date.now(),
    //   }
    //   sessionStorage.setItem(storageKey, JSON.stringify(draft))
    // } catch {
    //   // Quota / disabled — non-fatal, just don't persist.
    // }
  }, [state, hydrated, storageKey])

  const actions = useMemo(
    () => ({
      setVin: (vin: string) => dispatch({ type: "set-vin", vin }),
      setVehicle: (vehicle: VinDecodedVehicle | null) =>
        dispatch({ type: "set-vehicle", vehicle }),
      addPhoto: (photo: QEPhoto) => dispatch({ type: "add-photo", photo }),
      updatePhoto: (id: string, patch: Partial<QEPhoto>) =>
        dispatch({ type: "update-photo", id, patch }),
      removePhoto: (id: string) => dispatch({ type: "remove-photo", id }),
      toggleDamageTag: (panel: PanelSpec) =>
        dispatch({ type: "toggle-damage-tag", panel }),
      removeDamageTag: (panelId: string) =>
        dispatch({ type: "remove-damage-tag", panelId }),
      updateDamageTag: (panelId: string, patch: Partial<DamageTag>) =>
        dispatch({ type: "update-damage-tag", panelId, patch }),
      setNotes: (notes: string) => dispatch({ type: "set-notes", notes }),
      setCustomer: (customer: Partial<QuickEstimateCustomer>) =>
        dispatch({ type: "set-customer", customer }),
      setStep: (step: QuickEstimateStepIndex) =>
        dispatch({ type: "set-step", step }),
      reset: () => {
        try {
          sessionStorage.removeItem(storageKey)
        } catch {
          // ignore
        }
        dispatch({ type: "reset" })
      },
    }),
    [storageKey],
  )

  const photoCount = state.photos.length
  const hasAnyPhoto = useCallback(() => photoCount > 0, [photoCount])
  const submittablePhotoCount = state.photos.filter(
    (p) => p.uploadedUrl.length > 0 || p.sendWithoutAiTag,
  ).length

  return {
    state,
    actions,
    photoCount,
    submittablePhotoCount,
    hasAnyPhoto,
    hydrated,
  }
}

export type QuickEstimateActions = ReturnType<
  typeof useQuickEstimateState
>["actions"]
